// client/src/components/admin/AdminPendingAppointments.jsx
import React, { useState } from 'react';
import { format } from 'date-fns';
import adminService from '../../services/adminService';

import '../../styles/components/admin/AdminPendingAppointments.css';

const AdminPendingAppointments = ({ 
  pendingAppointments, 
  patient, 
  tenantId, 
  onAppointmentUpdated,
  onViewAppointmentDetails 
}) => {
  // Modal states
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [responseAction, setResponseAction] = useState(''); // 'accept' or 'decline'
  const [isProcessing, setIsProcessing] = useState(false);

  // Format appointment time
  const formatAppointmentTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const time = format(date, 'h:mm a');
      
      if (isToday(date)) {
        return `Today at ${time}`;
      } else if (isTomorrow(date)) {
        return `Tomorrow at ${time}`;
      } else {
        return format(date, 'MMM dd, yyyy') + ' at ' + time;
      }
    } catch (error) {
      return dateString;
    }
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is tomorrow
  const isTomorrow = (date) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.toDateString() === tomorrow.toDateString();
  };

  // Handle opening the response modal
  const openResponseModal = (appointment, action) => {
    setSelectedAppointment(appointment);
    setResponseAction(action);
    setResponseMessage('');
    setShowResponseModal(true);
  };

  // Handle closing the response modal
  const closeResponseModal = () => {
    setShowResponseModal(false);
    setSelectedAppointment(null);
    setResponseMessage('');
    setResponseAction('');
  };

  // Handle appointment response (accept/decline)
  const handleAppointmentResponse = async () => {
    if (!selectedAppointment || !responseAction) return;
    
    setIsProcessing(true);
    
    try {
      let response;
      
      if (responseAction === 'accept') {
        response = await adminService.acceptAppointmentRequest(
          selectedAppointment._id, 
          responseMessage || 'Appointment request approved by administrator.',
          tenantId
        );
      } else {
        response = await adminService.declineAppointmentRequest(
          selectedAppointment._id, 
          responseMessage || 'Appointment request declined by administrator.',
          tenantId
        );
      }
      
      if (response.success) {
        // Show success message
        alert(`Appointment ${responseAction === 'accept' ? 'accepted' : 'declined'} successfully!`);
        
        // Notify parent component to refresh data
        if (onAppointmentUpdated) {
          onAppointmentUpdated();
        }
        
        // Close the modal
        closeResponseModal();
      } else {
        alert(`Failed to ${responseAction} appointment: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`Error ${responseAction}ing appointment:`, error);
      alert(`Error ${responseAction}ing appointment: ${error.message || 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // If no pending appointments, return null
  if (!pendingAppointments || pendingAppointments.length === 0) {
  return (
    <div className="admin-pending-appointments-section">
      <div className="admin-pending-appointments-header">
        <h3 className="admin-pending-appointments-title">
          Pending Appointment Requests
        </h3>
        <div className="admin-pending-appointments-subtitle">
          No pending appointment requests at this time.
        </div>
      </div>
      
      <div className="admin-pending-appointments-empty-state">
        <div className="admin-pending-appointments-empty-icon">
          📅
        </div>
        <h4 className="admin-pending-appointments-empty-title">
          No Pending Requests
        </h4>
        <p className="admin-pending-appointments-empty-message">
          {patient?.firstName || 'This patient'} has no pending appointment requests waiting for approval.
        </p>
      </div>
    </div>
  );
}

  return (
    <>
      <div className="admin-pending-appointments-section">
        <div className="admin-pending-appointments-header">
          <h3 className="admin-pending-appointments-title">
            Pending Appointment Requests 
          </h3>
        </div>

        <div className="admin-pending-appointments-table">
          <div className="admin-pending-appointments-table-header">
            <div className="admin-pending-appointments-header-cell">Date & Time</div>
            <div className="admin-pending-appointments-header-cell">Doctor</div>
            <div className="admin-pending-appointments-header-cell">Type</div>
            <div className="admin-pending-appointments-header-cell">Duration</div>
            <div className="admin-pending-appointments-header-cell">Status</div>
            <div className="admin-pending-appointments-header-cell">Actions</div>
          </div>

          {pendingAppointments.map((appointment) => (
            <div key={appointment._id} className="admin-pending-appointments-table-row">
              <div className="admin-pending-appointments-table-cell">
                <div className="admin-pending-appointments-datetime">
                  <span className="admin-pending-appointments-date">
                    {formatAppointmentTime(appointment.appointmentDate)}
                  </span>
                  <span className="admin-pending-appointments-requested">
                    Requested: {format(new Date(appointment.createdAt), 'MMM dd, h:mm a')}
                  </span>
                </div>
              </div>
              
              <div className="admin-pending-appointments-table-cell">
                <div className="admin-pending-appointments-doctor">
                  <span className="admin-pending-appointments-doctor-name">
                    Dr. {appointment.doctor?.firstName} {appointment.doctor?.lastName}
                  </span>
                  <span className="admin-pending-appointments-doctor-specialty">
                    {appointment.doctor?.specialty || appointment.doctor?.specialization || 'General Practice'}
                  </span>
                </div>
              </div>
              
              <div className="admin-pending-appointments-table-cell">
                <span className="admin-pending-appointments-type">
                  {appointment.appointmentType || 'General Consultation'}
                </span>
              </div>
              
              <div className="admin-pending-appointments-table-cell">
                <span className="admin-pending-appointments-duration">
                  {appointment.duration || 30} min
                </span>
              </div>
              
              <div className="admin-pending-appointments-table-cell">
                <span className="admin-pending-appointments-status-badge admin-status-pending">
                  Pending Approval
                </span>
              </div>
              
              <div className="admin-pending-appointments-table-cell">
                <div className="admin-pending-appointments-actions">
                  <button
                    className="admin-pending-appointments-action-btn admin-pending-appointments-accept-btn"
                    onClick={() => openResponseModal(appointment, 'accept')}
                    disabled={isProcessing}
                  >
                    Accept
                  </button>
                  <button
                    className="admin-pending-appointments-action-btn admin-pending-appointments-decline-btn"
                    onClick={() => openResponseModal(appointment, 'decline')}
                    disabled={isProcessing}
                  >
                    Decline
                  </button>
                  <button
                    className="admin-pending-appointments-action-btn admin-pending-appointments-view-btn"
                    onClick={() => onViewAppointmentDetails && onViewAppointmentDetails(appointment)}
                    disabled={isProcessing}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Response Modal for Accept/Decline */}
      {showResponseModal && (
        <div className="admin-pending-appointments-modal-overlay">
          <div className="admin-pending-appointments-modal-container">
            <div className="admin-pending-appointments-modal-header">
              <h3 className="admin-pending-appointments-modal-title">
                {responseAction === 'accept' ? 'Accept' : 'Decline'} Appointment Request
              </h3>
              <button 
                className="admin-pending-appointments-modal-close"
                onClick={closeResponseModal}
                disabled={isProcessing}
              >
                ×
              </button>
            </div>
            
            <div className="admin-pending-appointments-modal-body">
              {selectedAppointment && (
                <div className="admin-pending-appointments-summary">
                  <h4>Appointment Details:</h4>
                  <div className="admin-pending-appointments-summary-grid">
                    <div className="admin-pending-appointments-summary-item">
                      <span className="admin-pending-appointments-summary-label">Patient:</span>
                      <span className="admin-pending-appointments-summary-value">
                        {patient.firstName} {patient.lastName}
                      </span>
                    </div>
                    <div className="admin-pending-appointments-summary-item">
                      <span className="admin-pending-appointments-summary-label">Doctor:</span>
                      <span className="admin-pending-appointments-summary-value">
                        Dr. {selectedAppointment.doctor?.firstName} {selectedAppointment.doctor?.lastName}
                      </span>
                    </div>
                    <div className="admin-pending-appointments-summary-item">
                      <span className="admin-pending-appointments-summary-label">Date & Time:</span>
                      <span className="admin-pending-appointments-summary-value">
                        {formatAppointmentTime(selectedAppointment.appointmentDate)}
                      </span>
                    </div>
                    <div className="admin-pending-appointments-summary-item">
                      <span className="admin-pending-appointments-summary-label">Type:</span>
                      <span className="admin-pending-appointments-summary-value">
                        {selectedAppointment.appointmentType || 'General Consultation'}
                      </span>
                    </div>
                    <div className="admin-pending-appointments-summary-item">
                      <span className="admin-pending-appointments-summary-label">Duration:</span>
                      <span className="admin-pending-appointments-summary-value">
                        {selectedAppointment.duration || 30} minutes
                      </span>
                    </div>
                    {selectedAppointment.notes && (
                      <div className="admin-pending-appointments-summary-item admin-pending-appointments-full-width">
                        <span className="admin-pending-appointments-summary-label">Patient Notes:</span>
                        <span className="admin-pending-appointments-summary-value">
                          {selectedAppointment.notes}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="admin-pending-appointments-form-group">
                <label htmlFor="responseMessage" className="admin-pending-appointments-form-label">
                  Response Message (Optional)
                </label>
                <textarea
                  id="responseMessage"
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder={`Leave a message about ${responseAction === 'accept' ? 'accepting' : 'declining'} this appointment...`}
                  rows={3}
                  className="admin-pending-appointments-form-textarea"
                  disabled={isProcessing}
                />
                <div className="admin-pending-appointments-form-hint">
                  This message will be sent to both the patient and doctor.
                </div>
              </div>
            </div>
            
            <div className="admin-pending-appointments-modal-footer">
              <button 
                className="admin-pending-appointments-btn admin-pending-appointments-btn-secondary"
                onClick={closeResponseModal}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button 
                className={`admin-pending-appointments-btn ${
                  responseAction === 'accept' 
                    ? 'admin-pending-appointments-btn-success' 
                    : 'admin-pending-appointments-btn-danger'
                }`}
                onClick={handleAppointmentResponse}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <span>
                    <span className="admin-pending-appointments-spinner"></span>
                    Processing...
                  </span>
                ) : (
                  `${responseAction === 'accept' ? 'Accept' : 'Decline'} Appointment`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPendingAppointments;